import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useLocalStorage } from "@rehooks/local-storage";
import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import { getFiltersPredicationFromURI, getSortOptionFromURI } from "components/Filters/helpers";
import { getSearchQuery } from "components/SearchInput/helpers";
import { UsePaginationFetchMoreProps } from "components/Table/Pagination/types";
import { SearchPoliciesOutput, SearchQueryRequestedPage } from "types/generated";

import {
  ENTITIES_PER_PAGE,
  EXCLUDE_LOGIN_PREDICATE,
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  POLICIES_LIMIT_STORAGE_KEY,
} from "./constants";
import { SEARCH_POLICIES } from "./gql";

const useSearchPolicies = () => {
  const [storedRowsPerPage] = useLocalStorage<number>(
    POLICIES_LIMIT_STORAGE_KEY,
    ENTITIES_PER_PAGE
  );
  const [after, setAfter] = useState<string | null>(null);
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage>();

  const [urlParams] = useSearchParams();
  const searchInput = getSearchQuery(urlParams);

  const sortOptionFields = useMemo(
    () => getSortOptionFromURI(urlParams, INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION),
    [urlParams]
  );

  const { predicates, hasAppliedFilters } = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams, true);

    return {
      predicates: [...(predicatesMap?.values() || []), EXCLUDE_LOGIN_PREDICATE],
      hasAppliedFilters: (predicatesMap?.size ?? 0) > 0,
    };
  }, [urlParams]);

  const paginationResetKey = useMemo(
    () => ({ searchInput, predicates, sortOptionFields }),
    [searchInput, predicates, sortOptionFields]
  );
  const [previousResetKey, setPreviousResetKey] = useState(paginationResetKey);

  if (!isEqual(previousResetKey, paginationResetKey)) {
    setPreviousResetKey(paginationResetKey);
    setAfter(null);
    setRequestedPage(undefined);
  }

  const variables = useMemo(
    () => ({
      input: {
        predicates,
        fullTextSearch: searchInput,
        first: storedRowsPerPage || ENTITIES_PER_PAGE,
        after,
        requestedPage,
        ...(sortOptionFields && { orderBy: sortOptionFields }),
      },
    }),
    [predicates, searchInput, storedRowsPerPage, after, requestedPage, sortOptionFields]
  );

  const { error, loading, data, previousData, networkStatus } = useQuery<{
    searchPolicies: SearchPoliciesOutput;
  }>(SEARCH_POLICIES, {
    variables,
    nextFetchPolicy: "cache-first",
  });

  const searchPolicies = data?.searchPolicies || previousData?.searchPolicies;

  const loadPolicies = useCallback(({ cursor, requestedPage }: UsePaginationFetchMoreProps) => {
    setAfter(cursor);
    setRequestedPage(requestedPage);
  }, []);

  const entities = useMemo(
    () => searchPolicies?.edges.map((edge) => edge.node) || [],
    [searchPolicies?.edges]
  );

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables);

  return {
    entities,
    error,
    loadingContent,
    loadPolicies,
    isPageEmpty: !!(data && !entities.length && !searchInput && !hasAppliedFilters),
    hasNoFilteringResults: !!data && !entities.length && (!!searchInput || hasAppliedFilters),
    startCursor: searchPolicies?.pageInfo.startCursor,
    endCursor: searchPolicies?.pageInfo.endCursor,
    paginationResetKey,
  };
};

export default useSearchPolicies;
